import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore.js';
import { servicioEnMesa } from '../lib/plan.js';

// Para qué sirve ESTE aparato.
//
//   caja       — la tablet del mostrador: abre turno, cobra, hace el corte.
//   comandera  — el celular del mesero: levanta órdenes y ya.
//
// La distinción no es cosmética. Una sesión de POS está atada a un
// `terminal_id` y a un `opening_cash`: es un cajón de dinero físico. Un mesero
// con su teléfono no tiene cajón, así que obligarlo a "abrir turno" para poder
// tomar una orden no tiene sentido — y es justo lo que hacía `SessionRoute`.
//
// Se guarda por dispositivo y no por usuario: el mismo mesero puede cobrar en
// la tablet del mostrador y ordenar desde su celular en la misma jornada.

const ANCHO_TELEFONO = 768;

// Primera vez en este aparato: se adivina por el tamaño de la pantalla, que
// acierta casi siempre. Es solo el valor inicial — se puede cambiar a mano y
// esa elección manda desde entonces.
const modoInicial = () => {
  if (typeof window === 'undefined') return 'caja';
  return window.innerWidth < ANCHO_TELEFONO ? 'comandera' : 'caja';
};

export const useDeviceModeStore = create(
  persist(
    (set) => ({
      modo:     modoInicial(),
      elegido:  false,        // true en cuanto alguien lo fija a mano

      // Tablet que usan varios meseros por turnos. En un restaurante lo normal
      // es que haya una o dos para todo el salón, y que la distinción de quién
      // captura sea el PIN, no la sesión: el aparato entra una vez en la mañana
      // y cada mesero se identifica al tomar una mesa.
      //
      // Va por aparato y no por comercio porque así es la realidad: la tablet
      // de la barra se comparte, la del gerente no.
      compartida: false,
      setCompartida: (v) => set({ compartida: !!v }),

      setModo:  (modo) => set({ modo, elegido: true }),
    }),
    { name: 'gc-pos-modo' }
  )
);

export const esCompartida = () => useDeviceModeStore.getState().compartida;

// Una comandera solo existe donde hay servicio en mesa. En una tienda —o en un
// plan sin mesas— el celular es una caja más, por chica que sea su pantalla:
// mandarlo al plano de mesas era dejarlo frente a una pantalla que no puede usar.
export const modoEfectivo = (modo, tenant) =>
  (modo === 'comandera' && !servicioEnMesa(tenant) ? 'caja' : modo);

// El modo con el que ESTE aparato trabaja de verdad. Todo lo que decide qué
// puede hacer el aparato pregunta aquí, no al valor guardado.
export const usarModo = () => {
  const modo   = useDeviceModeStore((s) => s.modo);
  const tenant = useAuthStore((s) => s.tenant);
  return modoEfectivo(modo, tenant);
};

// Un restaurante que todavía no dijo para qué es este aparato. Solo ahí importa:
// en una tienda todo aparato es caja y no hay nada que preguntar.
export const usarFaltaElegirModo = () => {
  const elegido = useDeviceModeStore((s) => s.elegido);
  const tenant  = useAuthStore((s) => s.tenant);
  const dentro  = useAuthStore((s) => s.isAuthenticated);
  return dentro && !elegido && servicioEnMesa(tenant);
};

export const esComandera = () =>
  modoEfectivo(useDeviceModeStore.getState().modo, useAuthStore.getState().tenant) === 'comandera';
